import { useState } from "react";
import { ShoppingCart, Radio } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Badge } from "@/components/ui/badge";
import { useOrders, useOrdersStats, OrdersFilters } from "@/hooks/useOrders";
import { useOrdersRealtime } from "@/hooks/orders/useOrdersRealtime";
import { useDebounce } from "@/hooks/useDebounce";
import OrdersSyncBanner from "@/components/orders/OrdersSyncBanner";
import OrdersStats from "@/components/orders/OrdersStats";
import OrdersFiltersComponent from "@/components/orders/OrdersFilters";
import OrdersTable from "@/components/orders/OrdersTable";
import OrderDetailSheet from "@/components/orders/OrderDetailSheet";
import SessionDebugStrip from "@/components/orders/SessionDebugStrip";

const Orders = () => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("processing_awaiting_shipment");
  const [dateFilter, setDateFilter] = useState("all");
  const [selectedOrderNumber, setSelectedOrderNumber] = useState<string | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  const filters: OrdersFilters = {
    search: debouncedSearch,
    statusFilter,
    dateFilter,
  };

  const { data: orders, isLoading: ordersLoading, error: ordersError } = useOrders(filters);
  const { data: stats, isLoading: statsLoading } = useOrdersStats();

  // Set up realtime subscription
  useOrdersRealtime();


  const isMobile = useIsMobile();

  return (
    <div className={isMobile ? "space-y-3" : "space-y-6"}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <ShoppingCart className={`${isMobile ? 'h-5 w-5' : 'h-6 w-6'} text-primary`} />
          </div>
          <div>
            <h1 className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold`}>Orders</h1>
            {!isMobile && (
              <p className="text-muted-foreground">
                Realtime overzicht van Lightspeed orders
              </p>
            )}
          </div>
        </div>
        <Badge variant="outline" className="flex items-center gap-1.5">
          <Radio className="h-3 w-3 text-green-500 animate-pulse" />
          Live
        </Badge>
      </div>

      {/* Sync Status Banner */}
      <OrdersSyncBanner />

      {/* Session Debug Strip */}
      <SessionDebugStrip 
        ordersCount={orders?.length} 
        isLoading={ordersLoading} 
        error={ordersError as Error | null} 
      />

      {/* Stats */}
      <OrdersStats stats={stats} isLoading={statsLoading} />

      {/* Filters */}
      <OrdersFiltersComponent
        search={search}
        onSearchChange={setSearch}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        dateFilter={dateFilter}
        onDateFilterChange={setDateFilter}
      />

      {/* Results count */}
      {!ordersLoading && orders && (
        <p className="text-sm text-muted-foreground">
          {orders.length} orderregel{orders.length !== 1 ? "s" : ""} gevonden
        </p>
      )}

      {/* Table */}
      <OrdersTable 
        orders={orders} 
        isLoading={ordersLoading} 
        onOrderClick={setSelectedOrderNumber}
      />

      {/* Order Detail Sheet */}
      <OrderDetailSheet
        ordernumber={selectedOrderNumber}
        open={!!selectedOrderNumber}
        onOpenChange={(open) => !open && setSelectedOrderNumber(null)}
      />
    </div>
  );
};

export default Orders;
